import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ndfApi } from '../../../services/ndf-api';
import { useUser } from '../../../context/UserContext';

import { BoutonSecondaire, Selection } from './Champ';
import { changerApercu, useApercu } from './useApercu';
import { apercuActif } from '../../../services/apercu';

/**
 * Aperçu de rôle — voir le module tel qu'une autre personne le voit.
 *
 * Ce n'est PAS une simulation d'affichage : l'en-tête envoyé au serveur fait
 * jouer les vraies projections. Ce qui s'affiche est donc exactement ce que la
 * personne verrait, y compris les champs que le serveur lui retire.
 *
 * En production, toute modification est refusée côté serveur : l'aperçu sert à
 * VOIR. En pré-production, il devient un BAC À SABLE — on agit réellement au
 * nom de la personne endossée, ce qui permet de dérouler le circuit complet
 * (déclarer, soumettre, approuver) depuis un seul compte Google.
 *
 * Le bandeau est permanent et volontairement voyant : sans repère, on finit par
 * croire qu'on regarde ses propres droits — et par s'étonner de ne plus rien
 * pouvoir faire, ou pire, de ne pas voir qu'on écrit chez quelqu'un d'autre.
 */
export function BarreApercu({ observee }: { observee?: { prenom: string; nom: string; id: string } | null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // L'aperçu porte sur un COMPTE, pas sur une fiche salarié : c'est le compte
  // qui détient le profil, le rôle et le rattachement hiérarchique.
  const { data } = useQuery({
    queryKey: ['ndf', 'utilisateurs'],
    queryFn: () => ndfApi.utilisateurs(),
    retry: false,
  });
  const utilisateurs = data?.utilisateurs ?? [];
  const { data: moi } = useQuery({ queryKey: ['ndf', 'moi', apercuActif()], queryFn: () => ndfApi.moi(), retry: false });
  const bacASable = Boolean(moi?.bac_a_sable);
  const actif = useApercu();
  // L'identité RÉELLE, et non celle du module : en aperçu, `moi` désigne la
  // personne observée. S'y fier ferait disparaître cette barre dès l'activation
  // — donc aussi le bouton pour en sortir.
  const { user } = useUser();

  if (user?.role !== 'admin') return null;

  async function changer(id: string | null) {
    // L'ORDRE COMPTE. Annoncer le changement d'identité en premier notifiait
    // les abonnés, donc redessinait l'écran AVANT que le cache ne soit vidé :
    // l'accueil se rendait alors avec les droits de l'OBSERVATEUR et le drapeau
    // d'aperçu DÉJÀ POSÉ. D'où un état hybride — les modules RH de
    // l'observateur, mais plus la section Finance — qui se corrigeait tout seul
    // à la première navigation, ce qui le rendait difficile à croire.
    //
    // On vide donc tout AVANT d'annoncer : aucun rendu ne peut plus mélanger
    // deux identités.
    //
    // TOUT le cache dépend de l'identité, sans exception : on le vide en
    // entier.
    //
    // La version précédente énumérait les préfixes concernés (« ndf »,
    // « accueil ») et avait oublié « conges » : l'endossement paraissait alors
    // ne marcher qu'une fois sur deux, selon ce qui était déjà en cache, et le
    // seul contournement fiable était d'arrêter l'aperçu puis de recharger la
    // page. Énumérer les modules, c'est se condamner à en oublier un — et le
    // prochain module écrit reproduirait le défaut en silence.
    //
    // Les requêtes en vol sont annulées d'abord : parties sous l'identité
    // PRÉCÉDENTE, leur réponse viendrait repeupler le cache qu'on vient de
    // vider, avec les données de la mauvaise personne.
    await qc.cancelQueries();
    qc.clear();
    changerApercu(id);
    // Puis on repart de l'accueil : l'écran où l'on se trouvait appartenait à
    // l'identité PRÉCÉDENTE. Rester dessus affichait « Salariés » à qui n'y a
    // pas droit, ou un écran refusé par le serveur juste après le changement.
    navigate('/accueil');
  }

  const personne = utilisateurs.find((u) => u.id === actif);
  // Quand on consulte la fiche de QUELQU'UN D'AUTRE pendant l'aperçu, l'écran
  // devient ambigu : on croit avoir changé d'identité alors qu'on regarde
  // simplement ce que la personne prévisualisée voit de son collègue.
  const ficheTierce = Boolean(actif && observee && observee.id !== actif);

  if (actif) {
    const qui = personne?.nom ?? personne?.email ?? 'cette personne';
    return (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-violet-fond border-l-4 border-l-violet-fond bg-white px-4 py-3">
        <p className="text-sm text-nuit">
          {bacASable ? (
            <>
              <span className="font-titre font-bold text-violet">Bac à sable</span> : vous utilisez
              l’application <strong>en tant que {qui}</strong>. Tout ce que vous faites ici (dépenses,
              notes de frais, approbations) est enregistré sur son compte, sur l’environnement de
              test uniquement.
            </>
          ) : (
            <>
              <span className="font-titre font-bold text-violet">Aperçu en cours</span> : vous voyez
              l’application telle que <strong>{qui}</strong> la voit. Aucune modification n’est
              possible dans ce mode.
            </>
          )}
          {ficheTierce && (
            <>
              {' '}
              Vous consultez ici la fiche de{' '}
              <strong>
                {observee?.prenom} {observee?.nom}
              </strong>{' '}
, telle qu’elle lui apparaît, et non en tant que cette personne.
            </>
          )}
        </p>
        <BoutonSecondaire type="button" onClick={() => void changer(null)}>
          {bacASable ? 'Revenir à mon compte' : 'Quitter l’aperçu'}
        </BoutonSecondaire>
      </div>
    );
  }

  return (
    <div className="mb-4 flex flex-wrap items-end gap-2">
      <label className="text-sm text-nuit/70">
        <span className="mb-1 block">
          {bacASable ? 'Utiliser l’application en tant que' : 'Voir l’application en tant que'}
        </span>
        <Selection
          className="w-auto min-w-64"
          value=""
          onChange={(e) => e.target.value && void changer(e.target.value)}
        >
          <option value="">Moi-même</option>
          {utilisateurs.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nom ?? u.email}
              {u.profil ? ` · ${u.profil}` : ''}
            </option>
          ))}
        </Selection>
      </label>
    </div>
  );
}
